import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface MemoryCarouselSectionProps {
  photos: string[];
  captions: string[];
  language: 'en' | 'mr';
}

export const MemoryCarouselSection: React.FC<MemoryCarouselSectionProps> = ({ photos, captions, language }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  if (!photos || photos.length === 0) return null;

  const currentPhoto = photos[currentIndex];
  const currentCaption = captions?.[currentIndex] || `Memory ${currentIndex + 1}`;

  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-center text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-pink-400 mb-12">
          {language === 'mr' ? 'आमचे क्षण' : 'Our Memories'}
        </h2>

        <div className="relative group">
          {/* Photo Container with 3D Effect */}
          <div className="relative mx-auto max-w-2xl aspect-video">
            {/* Glow Effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-2xl blur-2xl opacity-75 group-hover:opacity-100 transition-opacity duration-300 animate-pulse" />

            {/* Photo */}
            <div className="relative w-full h-full bg-slate-900 rounded-2xl overflow-hidden border-4 border-slate-800">
              <img
                src={currentPhoto}
                alt={currentCaption}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />

              {/* Overlay Caption */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-transparent to-transparent p-6">
                <p className="text-white text-xl font-bold drop-shadow-lg">
                  {currentCaption}
                </p>
              </div>

              {/* Photo Counter */}
              <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md px-3 py-1 rounded-full text-sm text-cyan-300 font-semibold border border-cyan-400">
                {currentIndex + 1} / {photos.length}
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={goToPrevious}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-16 p-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full hover:shadow-lg hover:shadow-cyan-500/50 transition-all transform hover:scale-125 z-10"
          >
            <ChevronLeft size={24} />
          </button>

          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-16 p-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full hover:shadow-lg hover:shadow-pink-500/50 transition-all transform hover:scale-125 z-10"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Thumbnail Strip */}
        <div className="flex justify-center gap-3 mt-12 overflow-x-auto pb-2">
          {photos.map((photo, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all transform hover:scale-110 ${
                idx === currentIndex
                  ? 'border-cyan-400 shadow-lg shadow-cyan-400'
                  : 'border-slate-600 hover:border-pink-400'
              }`}
            >
              <img src={photo} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>

        {/* Progress Bar */}
        <div className="mt-12 h-1 bg-slate-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-cyan-500 to-pink-500 transition-all duration-300"
            style={{ width: `${((currentIndex + 1) / photos.length) * 100}%` }}
          />
        </div>
      </div>
    </section>
  );
};
